import React, { useState } from 'react';
import AppStoreLink from '../../../components/common/AppStoreLink';

const APP_JOURNEY_SLIDES = [
  {
    id: 'home',
    step: '01',
    eyebrow: 'Start',
    title: 'Choose now or schedule',
    copy: 'Open the app and start from the move you need today, later today, or on another date.',
    image: '/images/landing/app-journey/01-home.jpeg',
    alt: 'ShiftWagon app home screen with instant and scheduled trip options',
  },
  {
    id: 'route',
    step: '02',
    eyebrow: 'Route',
    title: 'Set pickup and drop-off',
    copy: 'Choose both locations so the app can calculate the route distance and show an estimate.',
    image: '/images/landing/app-journey/02-route.jpeg',
    alt: 'ShiftWagon route screen for choosing pickup and drop-off locations',
  },
  {
    id: 'truck',
    step: '03',
    eyebrow: 'Vehicle',
    title: 'Pick the truck size',
    copy: 'Compare available vehicle types and see the route estimate before you continue.',
    image: '/images/landing/app-journey/03-choose-truck.jpeg',
    alt: 'ShiftWagon truck selection screen with vehicle options and an estimated fare',
  },
  {
    id: 'confirm',
    step: '04',
    eyebrow: 'Confirm',
    title: 'Review and authorize by card',
    copy: 'Confirm the trip details, notes, and card authorization before the booking is dispatched.',
    image: '/images/landing/app-journey/04-confirm.jpeg',
    alt: 'ShiftWagon booking confirmation screen with trip details and card authorization',
  },
  {
    id: 'searching',
    step: '05',
    eyebrow: 'Match',
    title: 'Find an available driver',
    copy: 'The app searches for a verified driver who can take the job.',
    image: '/images/landing/app-journey/05-searching.jpeg',
    alt: 'ShiftWagon searching screen while the app looks for an available driver',
  },
  {
    id: 'tracking',
    step: '06',
    eyebrow: 'Track',
    title: 'Follow the trip live',
    copy: 'Track driver progress from assignment through pickup, delivery, and completion.',
    image: '/images/landing/app-journey/06-live-tracking.jpeg',
    alt: 'ShiftWagon live tracking screen with a driver route on the map',
  },
  {
    id: 'details',
    step: '07',
    eyebrow: 'Details',
    title: 'Keep trip info close',
    copy: 'Pickup, drop-off, driver, vehicle, fare, and booking status stay in one place.',
    image: '/images/landing/app-journey/07-trip-details.jpeg',
    alt: 'ShiftWagon trip details screen showing booking information',
  },
  {
    id: 'messages',
    step: '08',
    eyebrow: 'Chat',
    title: 'Message inside the booking',
    copy: 'Use the trip conversation to clarify access notes or delivery instructions.',
    image: '/images/landing/app-journey/08-messages.jpeg',
    alt: 'ShiftWagon messages screen for a booking conversation',
  },
  {
    id: 'history',
    step: '09',
    eyebrow: 'History',
    title: 'Review previous trips',
    copy: 'Completed and cancelled trips remain available when you need to check past moves.',
    image: '/images/landing/app-journey/09-history.jpeg',
    alt: 'ShiftWagon trip history screen with previous bookings',
  },
];

const ProductPreviewSection: React.FC = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const activeSlide = APP_JOURNEY_SLIDES[activeIndex];

  const showPreviousSlide = () => {
    setActiveIndex((current) =>
      current === 0 ? APP_JOURNEY_SLIDES.length - 1 : current - 1,
    );
  };

  const showNextSlide = () => {
    setActiveIndex((current) =>
      current === APP_JOURNEY_SLIDES.length - 1 ? 0 : current + 1,
    );
  };

  return (
    <section className="sw-section sw-showcase">
      <div className="container sw-showcase-grid">
        <div>
          <span className="sw-eyebrow">On your phone</span>
          <h2>
            The whole move,
            <br />
            in your pocket.
          </h2>
          <p className="sw-showcase-copy">
            Swipe through the app flow: estimate, vehicle choice, card confirmation,
            driver matching, live tracking, messages, and trip history.
          </p>

          <AppStoreLink className="sw-btn-ink" placement="product_preview" />
          <p className="sw-showcase-note">Available on Google Play. iOS is on the way.</p>
        </div>

        <div className="sw-app-slider" aria-label="ShiftWagon app journey">
          <figure className="sw-app-slider-phone">
            <img
              alt={activeSlide.alt}
              className="sw-app-slider-image"
              src={activeSlide.image}
            />
          </figure>

          <div className="sw-app-slider-panel">
            <div className="sw-app-slider-head">
              <span className="sw-app-slider-step">{activeSlide.step}</span>
              <span className="sw-app-slider-kicker">{activeSlide.eyebrow}</span>
            </div>
            <h3>{activeSlide.title}</h3>
            <p>{activeSlide.copy}</p>

            <div className="sw-app-slider-arrows" aria-label="Choose app journey screen">
              <button
                aria-label="Show previous app screen"
                className="sw-app-slider-arrow"
                onClick={showPreviousSlide}
                type="button"
              >
                &lt;
              </button>
              <span className="sw-app-slider-count">
                {activeSlide.step} / {APP_JOURNEY_SLIDES.length.toString().padStart(2, '0')}
              </span>
              <button
                aria-label="Show next app screen"
                className="sw-app-slider-arrow"
                onClick={showNextSlide}
                type="button"
              >
                &gt;
              </button>
            </div>

            <div className="sw-app-slider-tabs" role="tablist" aria-label="App journey steps">
              {APP_JOURNEY_SLIDES.map((slide, index) => (
                <button
                  aria-label={`Show ${slide.title}`}
                  aria-selected={index === activeIndex}
                  className={`sw-app-slider-tab${
                    index === activeIndex ? ' sw-app-slider-tab-active' : ''
                  }`}
                  key={slide.id}
                  onClick={() => setActiveIndex(index)}
                  role="tab"
                  type="button"
                >
                  {slide.step}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProductPreviewSection;
